/**
 * What: Defines shared Shopify product language for embedded app surfaces.
 * Transport / data-flow: Pure TypeScript constants and lookup helpers consumed by Shopify app adapters and Worker payload builders.
 *
 */
import { shopifySyncLanguage } from './shopify-sync.js';
import { shopifyComponents } from './shopify-components.js';
import { shopifyStudyQuestionsLanguage } from './shopify-study-questions.js';
export const shopifyPageKeys = [
    'home',
    'study-builder',
    'study-run',
    'studies',
    'results',
    'micro-surveys',
    'pixel-events',
    'insights',
    'integrations',
    'settings',
];
export const shopifyAudienceModeKeys = [
    'broad_population',
    'all_customers_overlay',
    'repeat_customers',
    'high_value_customers',
    'lapsed_customers',
    'product_affinity_customers',
];
export const shopifyReadinessStatusKeys = ['unavailable', 'limited', 'ready'];
const pages = {
    home: {
        key: 'home',
        label: 'Home',
        navTitle: 'Home',
        title: 'TestFeed for Shopify',
        body: 'Plan product tests with source-backed Shopify product details and explicit customer background boundaries.',
    },
    'study-builder': {
        key: 'study-builder',
        label: 'Test Builder',
        navTitle: 'Test Builder',
        title: 'Build product test setup',
        body: 'Select a product, choose a test goal, then prepare a setup for TestFeed.',
    },
    'study-run': {
        key: 'study-run',
        label: 'Review and run test',
        navTitle: 'Review and run test',
        title: 'Review and run test',
        body: 'Confirm the saved setup, then start the test when you are ready.',
    },
    studies: {
        key: 'studies',
        label: 'Tests',
        navTitle: 'Tests',
        title: 'Review test lifecycle',
        body: 'Filter drafts, running tests, errors, and completed tests.',
    },
    results: {
        key: 'results',
        label: 'Test results',
        navTitle: 'Test results',
        title: 'Test results',
        body: 'Review the results and saved shopper answers for this test.',
    },
    'micro-surveys': {
        key: 'micro-surveys',
        label: 'Micro-surveys',
        navTitle: 'Micro-surveys',
        title: 'Manage storefront micro-surveys',
        body: 'Create and manage storefront survey payloads for supported Web Pixel triggers.',
    },
    'pixel-events': {
        key: 'pixel-events',
        label: 'Pixel events',
        navTitle: 'Pixel events',
        title: 'Review storefront Pixel events',
        body: 'Review recent storefront Web Pixel activity and survey request diagnostics.',
    },
    insights: {
        key: 'insights',
        label: 'Insights',
        navTitle: 'Insights',
        title: 'Insights',
        body: 'Who buys again, who is slipping away, and what they buy together.',
    },
    integrations: {
        key: 'integrations',
        label: 'Integrations',
        navTitle: 'Integrations',
        title: 'Manage integrations',
        body: 'Review connected data sources and open TestFeed only when an integration needs account-level setup.',
    },
    settings: {
        key: 'settings',
        label: 'Settings',
        navTitle: 'Settings',
        title: 'Configure TestFeed',
        body: 'Manage your TestFeed connection, available credits, and support options.',
    },
};
const components = shopifyComponents;
const audienceModes = {
    broad_population: {
        key: 'broad_population',
        label: 'Broad Shoppers',
        frameLabel: 'Broad shopper audience',
        frameDescription: 'Use a general shopper audience when store customer patterns are unavailable or not appropriate.',
    },
    all_customers_overlay: {
        key: 'all_customers_overlay',
        label: 'Customer Patterns',
        frameLabel: 'Customer-shaped audience',
        frameDescription: 'Use broad, non-personal Shopify customer patterns to shape the audience.',
    },
    repeat_customers: {
        key: 'repeat_customers',
        label: 'Repeat Customers',
        frameLabel: 'Repeat customer audience',
        frameDescription: 'Focus the audience on customers with repeat-purchase behavior when available.',
    },
    high_value_customers: {
        key: 'high_value_customers',
        label: 'Higher-Spend Customers',
        frameLabel: 'Higher-spend customer audience',
        frameDescription: 'Focus the audience on customers in higher spend ranges when available.',
    },
    lapsed_customers: {
        key: 'lapsed_customers',
        label: 'Lapsed Customers',
        frameLabel: 'Lapsed customer audience',
        frameDescription: 'Focus the audience on customers with inactive or win-back purchase patterns.',
    },
    product_affinity_customers: {
        key: 'product_affinity_customers',
        label: 'Product Interests',
        frameLabel: 'Product interest audience',
        frameDescription: 'Focus the audience on customers with prior interest in related products or categories.',
    },
};
const readinessReasons = {
    unavailable: 'No customer patterns are available yet, so the safest setup is a broad shopper audience.',
    limited: 'Some customer patterns are available, but coverage is limited. Use broad shoppers unless the test specifically needs store-shaped context.',
    ready: 'Customer patterns are available to shape a simulated audience for directional market research.',
};
const testFeedDisconnectedCopy = {
    title: 'TestFeed is disconnected',
};
export const productLanguageShopify = {
    components,
    pages,
    customerPatterns: {
        privacyNote: 'Broad, non-personal Shopify customer patterns can make simulated shoppers more relevant to your store. These are simulated shoppers, not a survey of your actual customers.',
        limitation: 'These patterns help shape shopper background for directional market research. They do not prove demographic truth or replace a matched customer panel.',
    },
    audienceBackground: {
        title: 'Ways To Shape The Test Audience',
        empty: 'No Shopify customer patterns are available yet.',
    },
    sync: shopifySyncLanguage,
    integrations: {
        status: {
            active: 'Active',
            notConnected: 'Not connected',
            needsAttention: 'Review needed',
        },
        actions: {
            manage: 'Manage in TestFeed',
            connectPosthog: 'Open TestFeed to connect',
            openTestFeed: 'Open TestFeed',
        },
        availability: {
            comingSoon: 'Coming soon',
            comingSoonDetail: 'This integration is not available yet.',
        },
        disconnected: {
            title: testFeedDisconnectedCopy.title,
            body: 'Finish the TestFeed account connection in Settings before managing integrations.',
        },
        loading: 'Loading integrations',
        loadFailedTitle: "Couldn't load integrations",
        loadFailedFallback: "We couldn't load integrations.",
        invalidResponse: 'Integrations response was invalid.',
        noAccountConnected: 'No account connected yet.',
        connectedBySuffix: 'connected by',
    },
    studyQuestions: shopifyStudyQuestionsLanguage,
    studyBuilder: {
        readinessBanners: {
            builderBlockedForTestFeedReadiness: {
                title: 'Connect TestFeed before preparing tests',
                body: 'Finish the TestFeed connection in Settings, then return here to prepare this product test setup.',
                action: 'Connect TestFeed in Settings',
            },
            notConnected: {
                title: 'Connect TestFeed workspace',
                action: 'Connect TestFeed',
            },
            disconnected: {
                title: testFeedDisconnectedCopy.title,
                body: 'Historical setups remain visible, but creating tests, opening TestFeed drafts, revising setups, and viewing TestFeed results require reconnecting in Settings.',
            },
            connectionSelectionRequired: {
                title: 'Choose a TestFeed workspace',
                body: 'This Shopify store is connected to more than one TestFeed workspace. Choose the workspace to use in Settings before creating or revising tests.',
            },
        },
        productStoryPlaceholders: [
            'Example: A lightweight linen shirt designed to stay crisp from work to weekend.',
            'Example: A refillable hand wash with a soft botanical scent and less plastic waste.',
            'Example: A compact standing desk converter that sets up quickly in small home offices.',
            'Example: A mineral sunscreen that feels breathable and leaves no sticky finish.',
            'Example: A meal kit for busy families who want fresh dinners with less prep.',
        ],
    },
    audienceModes,
    readinessReasons,
    storeIntelligence: {
        cards: {
            customerPatterns: {
                title: 'Customer Patterns',
                badge: 'Store data',
                tooltip: 'Broad customer-pattern counts from Shopify order and customer data.',
                description: 'Customer behavior patterns available to shape simulated shoppers.',
            },
            repeatCustomers: {
                title: 'Repeat Customers',
                badge: 'Purchase history',
                tooltip: 'Based on observed repeat-order behavior.',
                description: 'Customers with more than one observed order. Useful for retention, loyalty, and reorder tests.',
            },
            higherSpendCustomers: {
                title: 'Higher-Spend Customers',
                badge: 'Spend profile',
                tooltip: 'Based on relative spend bands, not individual customer targeting.',
                description: 'Customers in higher spend ranges. Useful for premium positioning and value-message research.',
            },
            productInterests: {
                title: 'Product Interests',
                badge: 'Buying interests',
                tooltip: 'Based on product and category interest patterns from store activity.',
                description: 'Customer patterns showing interest in specific products or categories. Useful for product extensions, bundles, cross-sells, and category-specific messaging.',
            },
        },
        distributions: {
            purchaseHistory: {
                title: 'Purchase History',
                dimensionLabel: 'Order count',
                sourceDescription: 'Observed Shopify order count.',
                emptyText: 'No usable purchase-history breakdown yet.',
            },
            spendLevels: {
                title: 'Spend Levels',
                dimensionLabel: 'Lifetime spend',
                sourceDescription: 'Observed Shopify lifetime spend.',
                emptyText: 'No usable spend-level breakdown yet.',
            },
            productInterests: {
                title: 'Product Interests',
                dimensionLabel: 'Interest',
                sourceDescription: 'Observed Shopify product affinity tags.',
                emptyText: 'No usable product-interest breakdown yet.',
            },
        },
    },
};
/** Returns customer-facing Shopify app shell language for a route key. */
export function getShopifyPageLanguage(key) {
    if (isShopifyPageKey(key))
        return productLanguageShopify.pages[key];
    return {
        key: 'unknown',
        navTitle: 'TestFeed for Shopify',
        label: 'TestFeed for Shopify',
        title: 'TestFeed for Shopify',
        body: 'Welcome to TestFeed in Shopify.',
    };
}
/** Returns merchant-facing language for Shopify audience modes. */
export function getShopifyAudienceModeLanguage(key) {
    if (isShopifyAudienceModeKey(key))
        return productLanguageShopify.audienceModes[key];
    return productLanguageShopify.audienceModes.broad_population;
}
/** Returns the merchant-facing readiness explanation for Shopify customer patterns. */
export function getShopifyReadinessReason(status) {
    return productLanguageShopify.readinessReasons[status];
}
function isShopifyPageKey(key) {
    return shopifyPageKeys.includes(key);
}
function isShopifyAudienceModeKey(key) {
    return shopifyAudienceModeKeys.includes(key);
}
//# sourceMappingURL=shopify.js.map